import { HttpClient, httpResource } from '@angular/common/http';
import {
  DOCUMENT,
  InjectionToken,
  Signal,
  WritableSignal,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
} from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import { Action, Selector, Store } from '@ngrx/store';
import { filter, map, switchMap, take, timeout, timer } from 'rxjs';

import {
  conformsTo,
  isArrayAndEveryElement,
  isBoolean,
  isDefined,
  isNullable,
  isNumber,
  isRecordWith,
  isString,
  matchesOneOf,
} from '@dmb/core';

import { GenreTree } from '../../components/genre-tree/genre-tree.component';
import { logError } from '../../helper/log.helper';
import { Country, CoverType, Genre, itunesIdForPricecode } from '../../model';
import { ContributorAssignment, ContributorEntry } from '../../model/product/contributors';
import { KIPolicies } from '../../model/product/track-metadata';
import { DmbWizardValue, WizardFeatureState, WizardStep } from '../../model/product/wizard';
import { skipHttpErrorPage } from '../../services/http-error/http-error.interceptor';
import { DBService } from '../../services/indexed-db';
import { WizardSessionActions } from './+state/app.actions';
import { WizardStore } from './+state/app.reducer';
import {
  combineAddableProductTitle,
  constraintSelectors,
  selectProductDomain,
  shopsResourceDependencies,
  valueSelectors,
} from './+state/app.selector';
import { ProductType } from './type/product-type-select.component';

export const COUNTRIES_RESOURCE = new InjectionToken('product_wizard_countries_resource', {
  providedIn: 'root',
  factory: () =>
    httpResource<Country[]>(() => ({ url: '/api/v5/locale/countries', context: skipHttpErrorPage() }), {
      parse: (countryList) => {
        if (
          !isArrayAndEveryElement(
            conformsTo({ id: isNumber, name: isString, iso3166_2: isString, continent_group: isString }),
          )(countryList)
        ) {
          const err = new Error('Invalid countries received');

          logError(err, { extra: { response: countryList } });
          throw err;
        }

        return countryList.map((c) => ({
          iso3166_2Code: c.iso3166_2,
          name: c.name,
          continent: c.continent_group,
          id: String(c.id),
        }));
      },
    }),
});

export const REGIONS_RESOURCE = new InjectionToken('product_wizard_regions_resource', {
  providedIn: 'root',
  factory: () => {
    return httpResource(() => ({ url: '/api/v5/locale/regions', context: skipHttpErrorPage() }), {
      parse: (regionList) => {
        if (
          !isArrayAndEveryElement(
            conformsTo({ id: isNumber, name: isString, countries_id: isArrayAndEveryElement(isNumber) }),
          )(regionList)
        ) {
          const err = new Error('Invalid regions received');

          logError(err, { extra: { response: regionList } });
          throw err;
        }

        return regionList
          .map((r) => ({ id: r.id, name: r.name, countries: r.countries_id.map(String) }))
          .sort((a, b) => {
            if (a.id === 1) {
              return -1;
            }
            if (b.id === 1) {
              return 1;
            }
            return a.name.localeCompare(b.name);
          });
      },
    });
  },
});

export const SHOPS_RESOURCE = new InjectionToken('product_wizard_retailers_resource', {
  providedIn: 'root',
  factory: () => {
    const http = inject(HttpClient);
    const store = inject<WizardStore>(Store);
    const allShops = store.selectSignal(valueSelectors.shopSelection.allShops);
    const shopDependencies = store.selectSignal(shopsResourceDependencies);
    const priceCodes = store.selectSignal(constraintSelectors.priceCodes);
    const itunesPriceCodes = store.selectSignal(constraintSelectors.itunesPriceCodes);

    return rxResource({
      params: () => {
        if (allShops()) {
          return undefined;
        }

        return albumsApiBody(shopDependencies(), {
          priceCodes: priceCodes(),
          itunesPriceCodes: itunesPriceCodes(),
        });
      },
      stream: ({ params: body }) =>
        // Debounce, to avoid requests on every keystroke
        timer(500).pipe(
          switchMap(() => http.post('/api/v5/audio/albums/retailers', body, { context: skipHttpErrorPage() })),
          map((result) => {
            if (
              !conformsTo({
                retailers: isArrayAndEveryElement(
                  conformsTo({
                    id: isNumber,
                    name: isString,
                    preselect_on_product: isBoolean,
                    product_use_types: isArrayAndEveryElement(isString),
                    user_defined_product_use_types: isNullable(isArrayAndEveryElement(conformsTo({ id: isString }))),
                    deliverable: isBoolean,
                    restrictions: isNullable(isArrayAndEveryElement(conformsTo({ msg: isString }))),
                  }),
                ),
              })(result)
            ) {
              const err = new Error('Invalid retailers received');

              logError(err, { extra: { response: result } });
              throw err;
            }

            return result.retailers
              .map((r) => ({
                id: r.id,
                name: r.name,
                preselect: r.preselect_on_product,
                download: r.deliverable && r.product_use_types.includes('PermanentDownload'),
                streaming: r.deliverable && r.product_use_types.includes('OnDemandStream'),
                ringtone: r.deliverable && r.product_use_types.includes('UseAsRingtone'),
                special:
                  r.deliverable && r.user_defined_product_use_types
                    ? r.user_defined_product_use_types.length > 0
                    : false,
                specialType: r.user_defined_product_use_types?.[0]?.id,
                deliverable: r.deliverable,
                errors: r.restrictions?.map((restriction) => restriction.msg) ?? [],
              }))
              .sort((a, b) => a.name.localeCompare(b.name));
          }),
        ),
    });
  },
});

export const DMB_GENRES = new InjectionToken('product_wizard_dmb_genres_resource', {
  providedIn: 'root',
  factory: () => {
    const productDomain = inject<WizardStore>(Store).selectSignal(selectProductDomain);
    return httpResource<GenreTree>(() => ({
      url: `/api/v1/products/genre-tree?genreSet=DMB&productDomains=${productDomain()}`,
      context: skipHttpErrorPage(),
    }));
  },
});

export const BISAC_GENRES = new InjectionToken('product_wizard_bisac_genres_resource', {
  providedIn: 'root',
  factory: () => {
    const productDomain = inject<WizardStore>(Store).selectSignal(selectProductDomain);
    return httpResource<GenreTree>(() => {
      const domain = productDomain();
      if (domain === 'MUSIC') {
        return undefined;
      }

      return {
        url: `api/v1/products/genre-tree?productDomains=${domain}&genreSet=BISAC&selectableSet=def&sortBy=label`,
        context: skipHttpErrorPage(),
      };
    });
  },
});

export const WGS_GENRES = new InjectionToken('product_wizard_wgs_genres_resource', {
  providedIn: 'root',
  factory: () => {
    const productDomain = inject<WizardStore>(Store).selectSignal(selectProductDomain);
    return httpResource<GenreTree>(() => {
      const domain = productDomain();
      if (domain === 'MUSIC') {
        return undefined;
      }

      return {
        url: `api/v1/products/genre-tree?productDomains=${domain}&genreSet=WGS&selectableSet=def&sortBy=label`,
        context: skipHttpErrorPage(),
      };
    });
  },
});

/**
 * Tracks expansion panels that toggle themselves, so their content is only rendered while expanded,
 * including the closing animation. Bind the panel to it:
 * [expanded]="panels.isExpanded(key, default)" (expandedChange)="panels.expandedChange(key, $event)"
 * (isOpen)="panels.toggled(key, $event)", and render the content only if panels.isRendered(key, default).
 */
export class LazyPanels<K> {
  private expanded = signal<ReadonlyMap<K, boolean>>(new Map());
  private open = signal<ReadonlySet<K>>(new Set());

  isExpanded(key: K, expandedByDefault: boolean) {
    return this.expanded().get(key) ?? expandedByDefault;
  }

  isRendered(key: K, expandedByDefault: boolean) {
    return this.isExpanded(key, expandedByDefault) || this.open().has(key);
  }

  expandedChange(key: K, expanded: boolean) {
    this.expanded.update((panels) => new Map(panels).set(key, expanded));
  }

  // The panel emits isOpen once its animation finished
  toggled(key: K, open: boolean) {
    this.open.update((panels) => {
      if (panels.has(key) === open) {
        return panels;
      }

      const result = new Set(panels);
      if (open) {
        result.add(key);
      } else {
        result.delete(key);
      }
      return result;
    });
  }
}

export function openPanel(document: Document, id: string) {
  const el = document.getElementById(id);
  console.assert(Boolean(el), 'Cannot find element for id:', id);
  el?.dispatchEvent(new CustomEvent('expansion_panel_open'));
}

export function closePanel(document: Document, id: string) {
  const el = document.getElementById(id);
  console.assert(Boolean(el), 'Cannot find element for id:', id);
  el?.dispatchEvent(new CustomEvent('expansion_panel_close'));
}

export function panelOpened(document: Document, id: string) {
  const element = document.getElementById(id);

  if (element?.getAttribute('expanded') === 'true') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    if (element) {
      const timeout = setTimeout(
        () => reject('Timeout'),
        // Using a high number here, to add some leeway.
        5000,
      );

      element.addEventListener(
        'expansion_panel_toggle_complete',
        () => {
          resolve();
          clearTimeout(timeout);
        },
        { once: true },
      );
    } else {
      reject('Element not found');
    }
  });
}

export function panelClosed(document: Document, id: string) {
  const element = document.getElementById(id);

  if (element?.getAttribute('expanded') === 'false') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    if (element) {
      const timeout = setTimeout(
        () => reject('Timeout'),
        // Using a high number here, to add some leeway.
        5000,
      );

      element.addEventListener(
        'expansion_panel_toggle_complete',
        () => {
          resolve();
          clearTimeout(timeout);
        },
        { once: true },
      );
    } else {
      reject('Element not found');
    }
  });
}

export function centerOn(document: Document, id: string, prefersReducedMotion: boolean) {
  const el = document.getElementById(id);
  console.assert(Boolean(el), 'Cannot find element for id:', id);
  el?.scrollIntoView({ behavior: prefersReducedMotion ? 'instant' : 'smooth', block: 'center' });
}

export const ACTIVE_STEP = new InjectionToken('product_wizard_active_step', {
  providedIn: 'root',
  // Start on sessions, if there are any, otherwise start on product type
  factory: () => {
    const dbService = inject(DBService);
    const sessionListResource = dbService.productWizardSessionList;
    const store = inject<WizardStore>(Store);

    let activeStep: WritableSignal<WizardStep>;

    if (sessionListResource.hasValue()) {
      activeStep = signal(
        sessionListResource.value().length > 0 ? WizardStep.SESSION_SELECT : WizardStep.PRODUCT_TYPE_SELECT,
      );
    } else if (dbService.status() === 'blocking') {
      activeStep = signal(WizardStep.SESSION_SELECT);
    } else {
      activeStep = signal(WizardStep.PRODUCT_TYPE_SELECT);

      // If session have not yet loaded, allow a short timespan to change decision
      toObservable(sessionListResource.value)
        .pipe(
          filter(isDefined),
          take(1),
          filter((l) => l.length > 0),
          timeout(1000),
        )
        .subscribe(() => activeStep.set(WizardStep.SESSION_SELECT));
    }

    effect(() => {
      const newStep = activeStep();

      if (newStep !== WizardStep.SESSION_SELECT) {
        store.dispatch(WizardSessionActions.stepChanged({ newStep }));
      }
    });

    return activeStep;
  },
});

export const PREFERS_REDUCED_MOTION = new InjectionToken('prefers_reduced_motion', {
  providedIn: 'root',
  factory: () => {
    const queryList = inject(DOCUMENT).defaultView?.matchMedia('(prefers-reduced-motion: reduce)');
    const currentState = signal(queryList?.matches ?? false);

    queryList?.addEventListener('change', (evt) => currentState.set(evt.matches));

    return currentState.asReadonly();
  },
});

/**
 * Expose stored value as WritableSignal.
 * Touched only updates correctly, if onBlur is connected.
 * rawErrors always returns errors, errors only returns them if touched.
 */
export function inputAccessor<T, ErrorType>(
  store: WizardStore,
  stepTouchedSelector: Selector<Record<string, unknown>, { touched: boolean }>,
  selector: Selector<Record<string, unknown>, T>,
  setAction: (args: { value: T }) => Action<string>,
  errorSelector: Selector<Record<string, unknown>, null | ErrorType> = () => null,
  onBlur?: (event: FocusEvent) => void,
) {
  const valueSignal = store.selectSignal(selector);
  const rawErrors = store.selectSignal(errorSelector);

  const touched = linkedSignal({
    source: store.selectSignal(stepTouchedSelector),
    computation: ({ touched }) => touched,
  });

  const errorSignal = computed(() => {
    return touched() ? rawErrors() : null;
  });

  const setValue = (value: T) => {
    const oldValue = untracked(() => valueSignal());
    if (oldValue !== value) {
      store.dispatch(setAction({ value }));
      touched.set(true);
    }
  };

  const updateValue = (update: (oldValue: T) => T) => {
    const oldValue = untracked(() => valueSignal());
    setValue(update(oldValue));
  };

  const innerOnBlur = (event: FocusEvent) => {
    touched.set(true);
    onBlur?.(event);
  };

  Object.defineProperties(valueSignal, {
    set: { value: setValue },
    update: { value: updateValue },
    errors: { value: errorSignal },
    rawErrors: { value: rawErrors },
    onBlur: { value: innerOnBlur },
    touched: { value: touched },
  });

  return valueSignal as WritableSignal<T> & {
    errors: Signal<null | ErrorType>;
    rawErrors: Signal<null | ErrorType>;
    onBlur: (event: FocusEvent) => void;
    touched: WritableSignal<boolean>;
  };
}

export function entryIsValid(entry: undefined | null | string[]): boolean {
  return !Array.isArray(entry) || entry.length === 0;
}

export function combineLists<T>(listA: T[] | null, listB: T[] | null): T[] | null {
  if (listA === null) {
    return listB;
  }

  if (listB === null) {
    return listA;
  }

  return listA.concat(listB);
}

export function generateTrackPanelTitle(
  index: number,
  { title, titleVersion }: { title: string; titleVersion: string },
) {
  if (titleVersion) {
    return `Track ${index + 1} - ${title} (${titleVersion})`;
  }

  return `Track ${index + 1} - ${title}`;
}

/**
 * @param value potential Artist ID or Profile URL
 * @returns Valid Id, if found, or null
 */
export function parseAppleArtistID(value: string): string | null {
  const matches = /\d{7,}/.exec(value);
  return matches?.[0] ?? null;
}

/**
 * @param value potential Artist ID, Profile URL or Profile URI
 * @returns Valid Id, if found, or null
 */
export function parseSpotifyArtistID(untrimmedValue: string): string | null {
  const value = untrimmedValue.trim();

  const plainMatches = /^\w{22,}$/.exec(value);
  if (plainMatches) {
    return plainMatches[0];
  }

  const uriMatches = /^spotify:(?:artist|user):(\w{22,})$/.exec(value);
  if (uriMatches) {
    return uriMatches[1];
  }

  const urlMatches = /^https?:\/\/open.spotify.com\/(?:intl-[a-z]{2,}\/)?artist\/(\w{22,})/.exec(value);
  if (urlMatches) {
    return urlMatches[1];
  }

  return null;
}

function formatFloat(value: string) {
  if (value.trim() === '') {
    // Has to be special case, because Number() considers whitespace as 0
    return undefined;
  }

  const parsed = Number(value.replace(',', '.'));
  if (Number.isNaN(parsed)) {
    return undefined;
  }

  return parsed;
}

export function pickPriceCode<PriceCode extends { consumer_price_min: number; consumer_price_max: number }>(
  priceCodes: PriceCode[],
  price: string | undefined,
) {
  if (priceCodes.length <= 1) {
    // undefined, if there are no price codes is fine
    return priceCodes[0];
  }

  if (price === undefined) {
    return undefined;
  }

  const parsedPrice = formatFloat(price);

  if (parsedPrice === undefined) {
    return undefined;
  }

  const matchingCodes = priceCodes.filter(
    (code) => code.consumer_price_min <= parsedPrice && parsedPrice <= code.consumer_price_max,
  );

  if (matchingCodes.length === 1) {
    return matchingCodes[0];
  }

  if (matchingCodes.length > 1) {
    // Pick the range with the highest minimum
    return matchingCodes.reduce((highest, current) =>
      highest.consumer_price_min > current.consumer_price_min ? highest : current,
    );
  }

  // Try to round up
  const higherCodes = priceCodes.filter((code) => code.consumer_price_min > parsedPrice);

  if (higherCodes.length === 1) {
    return higherCodes[0];
  }

  if (higherCodes.length > 1) {
    // Pick the closes one
    return higherCodes.reduce((closest, current) =>
      closest.consumer_price_min < current.consumer_price_min ? closest : current,
    );
  }

  // Cannot round up (above highest price range). Pick highest price
  return priceCodes.reduce((highest, current) =>
    highest.consumer_price_max > current.consumer_price_max ? highest : current,
  );
}

// IMPORTANT: Prices have to be sorted
export function pickPriceCodeItunes<PriceCode extends { dealer_price: number }>(
  priceCodes: PriceCode[],
  price: string | undefined,
) {
  if (priceCodes.length <= 1) {
    // undefined, if there are no price codes is fine
    return priceCodes[0];
  }

  if (price === undefined) {
    return undefined;
  }

  const parsedPrice = formatFloat(price);

  if (parsedPrice === undefined) {
    return undefined;
  }

  return priceCodes.find((code) => code.dealer_price >= parsedPrice);
}

function formatDate(date: Date | undefined) {
  if (date === undefined || Number.isNaN(date.getTime())) {
    return undefined;
  }

  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function mapContributors(
  assignments: ContributorAssignment[],
  contributors: ContributorEntry[],
): ProductContributorWriteDTO[] {
  return assignments.map((a) => {
    const entry = contributors.find((c) => c.id === a.id);
    if (!entry) {
      throw new Error('Can not find entry');
    }
    const parsedId = parseInt(entry.id, 10);

    return {
      contributor_id: Number.isNaN(parsedId) ? undefined : parsedId,
      name: entry.name,
      role_ids: a.roles.map((id) => parseInt(id, 10)),
      role_ai_levels: a.roles.map((id) => a.aiUsage?.[id] ?? KIPolicies.NONE),
    };
  });
}

export function parsedGenreId(genre: Genre | undefined): number | undefined {
  if (genre === undefined) {
    return undefined;
  }

  const parsedId = parseInt(genre.id, 10);
  return Number.isNaN(parsedId) ? undefined : parsedId;
}

export function pickGenres(
  isSpokenWord: boolean,
  { genre, bisacGenre, wgsGenre }: Pick<DmbWizardValue['productData'], 'genre' | 'bisacGenre' | 'wgsGenre'>,
) {
  const genre1 = parsedGenreId(genre[0]);
  let genre2;
  let genre3;
  if (isSpokenWord) {
    genre2 = parsedGenreId(bisacGenre[0]);
    genre3 = parsedGenreId(wgsGenre[0]);
  } else {
    genre2 = parsedGenreId(genre[1]);
    genre3 = parsedGenreId(genre[2]);
  }

  return [genre1, genre2, genre3];
}

type ProductContributorWriteDTO = {
  contributor_id?: number;
  name?: string;
  role_ids: number[];
  is_primary?: boolean; // Always undefined
  role_ai_levels: KIPolicies[];
};

type AlbumRequestDTO = {
  album_type?: ProductType;
  script_locale?: string;
  ean?: string;
  upc?: string;
  catalog_number?: string;
  title?: string;
  title_version?: string;
  preorder_start_date?: string;
  sales_start_date?: string;
  streaming_start_date?: string; // Always undefined, not part of wizard
  sales_end_date?: string; // Always undefined, not part of wizard
  original_release_date?: string;
  price_code?: string;
  price_code_itunes?: number;
  price_code_itunes_preorder?: number; // Always undefined, not part of wizard
  retail_price?: number;
  product_series_title?: string;
  product_series_number?: string;
  p_line?: string;
  c_line?: string;
  text_product?: {
    is_available: boolean;
    c_line?: string;
    release_date?: string;
  };
  physical_audio_product?: {
    is_available: boolean;
    isbn13?: string;
    release_date?: string;
    retail_price?: number;
  };
  product_description?: string;
  product_keywords?: string[];
  review_excerpt?: string;
  content_rating?:
    | 'NOT_EXPLICIT'
    | 'EXPLICIT'
    | 'CLEANED'
    | 'VIDEO_CLEAN__LYRICS_EXPLICIT'
    | 'VIDEO_EXPLICIT__LYRICS_CLEAN';
  audience_min_age?: number;
  is_abridged_version?: boolean;
  label_id?: number;
  contributors?: ProductContributorWriteDTO[];
  contributor_details?: {
    contributor_id?: number;
    name?: string;
    dsp_details: {
      dsp: 'SPOTIFY' | 'APPLE';
      artist_id: string;
      request_new_store_artist_id: boolean;
    }[];
  }[];
  genre1_id?: number;
  genre2_id?: number;
  genre3_id?: number;
  file_imports: {
    location_uri: string;
    mime_type: string;
    file_size?: number;
    checksum_md5?: string; // Always undefined, may be replaced with SHA-1
    original_file_name?: string;
    asset_type:
      | 'FRONT_COVER'
      | 'BACK_COVER'
      | 'PROMOTION'
      | 'BOOKLET'
      | 'SCREENSHOT'
      | 'OTHER'
      | 'AUDIO_PREVIEW'
      | 'CANVAS_VIDEO';
    // Needed for Booklet only
    asset_title?: string;
    asset_c_line?: string;
    ai_usage_level?: KIPolicies;
  }[];
  release_overrides: { dsp: 'META' | 'TIKTOK'; sales_start_date: string }[];
  release_countries_ids?: number[];
  retailers?: {
    retailer_id: string;
    allowed_product_use_types: ('OnDemandStream' | 'PermanentDownload' | 'UseAsRingtone' | string)[];
  }[];
  auto_deliver_to_new_retailers?: boolean;
  tracks?: {
    volume_no?: number; // Always undefined, not part of wizard
    track_no: number;
    clear_for_download?: boolean; // Always true, not part of wizard
    clear_for_streaming?: boolean; // Always true, not part of wizard
    clear_for_ringtone?: boolean; // Always true, not part of wizard
    clear_for_other_use_types?: boolean; // Always true, not part of wizard
    sales_start_date_override?: string;
    streaming_start_date_override?: string;
    track_id?: number; // For existing track
    track?: {
      // Left out for existing track
      track_type?: 'AUDIO' | 'VIDEO';
      track_subtype?: string; // Always undefined, backend figures it out
      isrc?: string;
      script_locale?: string;
      audio_locale?: string;
      title?: string;
      title_version?: string;
      title_work?: string; // Always undefined, not part of wizard
      price_code: string;
      p_line: string;
      publishers: string[];
      is_remasted_version?: boolean;
      remastered_year?: number;
      is_cover_version?: boolean;
      is_live_recording?: boolean;
      is_instrumental?: boolean;
      is_a_capella?: boolean;
      is_public_domain?: boolean;
      has_collecting_society?: boolean; // Always undefined, not part of wizard
      lyrics?: string;
      content_rating?:
        | 'NOT_EXPLICIT'
        | 'EXPLICIT'
        | 'CLEANED'
        | 'VIDEO_CLEAN__LYRICS_EXPLICIT'
        | 'VIDEO_EXPLICIT__LYRICS_CLEAN';
      aiUsage?: {
        ai_level_audio?: KIPolicies;
        ai_level_video?: KIPolicies;
        ai_level_lyrics?: KIPolicies;
        ai_level_composition?: KIPolicies;
      };
      preview_snippet_offset?: number;
      preview_snippet_duration?: number;
      is_apple_digital_master?: boolean;
      mastering_studio_email?: string;
      contributors: ProductContributorWriteDTO[];
      genre1_id?: number;
      genre2_id?: number;
      genre3_id?: number;
      neighbouring_rights?: undefined; // Always undefined, not part of wizard
      file_imports: {
        location_uri: string;
        mime_type: string;
        file_size?: number;
        checksum_md5?: string; // Always undefined, may be replaced with SHA-1
        original_file_name?: string;
        file_type: 'PRIMARY_TRACK_FILE';
        audio_format?: {
          duration?: number;
          container_format?: 'wav' | 'flac';
          codec?: string;
          channels?: number;
          sample_rate?: number;
          bits_per_sample?: 8 | 16 | 24 | 32;
          bitrate?: number;
        };
      }[];
    };
  }[];
  tracks_neighbouring_rights?: {
    country_id: number;
    percentage_own: number;
    evaluation_start_date: string;
    evaluation_end_date: string;
  }[];
};

export function albumsApiBody(
  {
    productType,
    productData,
    tracklisting,
    tracksProductTitleAdded,
    releaseDates,
    contributors,
    productContributors,
    overallMetadata,
    countrySelection,
    shopSelection,
    neighbouringRightsSelection,
    tracksPreview,
  }: Omit<DmbWizardValue, 'lastModified' | 'eanWasGenerated' | 'stepProgress'>,
  { priceCodes, itunesPriceCodes }: Pick<WizardFeatureState['constraints'], 'priceCodes' | 'itunesPriceCodes'>,
) {
  const isSpokenWord = productType === 'AUDIOBOOK' || productType === 'RADIO_PLAY';
  const hasVideo = false; // TODO: update once videos can be added

  const body: AlbumRequestDTO = {
    file_imports: [],
    release_overrides: [],
  };

  body.album_type = productType;
  body.script_locale = productData.language || undefined;

  if (productData.eanUpc.length === 12) {
    body.upc = productData.eanUpc;
  } else {
    body.ean = productData.eanUpc;
  }

  body.catalog_number = productData.catalogNumber;
  body.title = productData.title;
  body.title_version = productData.titleVersion;

  if (releaseDates.preOrderReleaseDateToggle) {
    body.preorder_start_date = formatDate(releaseDates.preOrderReleaseDate);
  }
  body.sales_start_date = formatDate(releaseDates.releaseDate);
  if (releaseDates.origReleaseDateToggle) {
    body.original_release_date = formatDate(releaseDates.origReleaseDate);
  }

  if (isSpokenWord) {
    body.retail_price = formatFloat(productData.digitalPrice);
    body.price_code = pickPriceCode(priceCodes, productData.digitalPrice)?.code;

    const itunesCode = pickPriceCodeItunes(itunesPriceCodes, productData.digitalPrice)?.code;

    if (itunesCode !== undefined) {
      const parsedCode = parseInt(itunesCode, 10);

      if (!Number.isNaN(parsedCode)) {
        body.price_code_itunes = parsedCode;
      }
    }

    body.product_series_title = productData.seriesName || undefined;
    body.product_series_number = productData.seriesNumber || undefined;
  } else {
    body.price_code = productData.price;
    body.price_code_itunes = itunesIdForPricecode[productData.price];
  }

  body.p_line =
    productData.pYear && productData.pText
      ? `${productData.pYear} ${productData.pText}`
      : productData.pYear || productData.pText;
  body.c_line =
    productData.cYear && productData.cText
      ? `${productData.cYear} ${productData.cText}`
      : productData.cYear || productData.cText;

  if (isSpokenWord) {
    body.text_product = {
      is_available: releaseDates.textReleaseDateToggle,
      release_date: formatDate(releaseDates.textReleaseDate),
      c_line:
        releaseDates.spokenWordCopyrightYear && releaseDates.spokenWordCopyrightReleaseDate
          ? `${releaseDates.spokenWordCopyrightYear} ${releaseDates.spokenWordCopyrightReleaseDate}`
          : releaseDates.spokenWordCopyrightYear || releaseDates.spokenWordCopyrightReleaseDate,
    };

    body.physical_audio_product = {
      is_available: releaseDates.physicalProductReleaseDateToggle,
      isbn13: releaseDates.isbn || undefined,
      release_date: formatDate(releaseDates.physicalProductReleaseDate),
      retail_price: formatFloat(releaseDates.price),
    };
  }

  body.product_description = productData.description;

  if (isSpokenWord) {
    if (productData.keywords) {
      body.product_keywords = productData.keywords.split(',');
    }

    body.review_excerpt = productData.reviewQuotes;
  }

  body.content_rating = overallMetadata.explicit;

  if (isSpokenWord) {
    const parsedAgeRating = parseInt(productData.ageRating, 10);

    if (!Number.isNaN(parsedAgeRating)) {
      body.audience_min_age = parsedAgeRating;
    }

    if (productData.version === 'abridged') {
      body.is_abridged_version = true;
    } else if (productData.version === 'unabridged') {
      body.is_abridged_version = false;
    }
  }

  const parsedLabelId = parseInt(productData.label, 10);
  if (!Number.isNaN(parsedLabelId)) {
    body.label_id = parsedLabelId;
  }

  body.contributors = mapContributors(productContributors, contributors);

  body.contributor_details = contributors
    .filter((entry) => {
      if (productContributors.some((pC) => pC.id === entry.id)) {
        return true;
      }
      if (tracklisting.some((track) => !track.existing && track.contributors.some((tC) => tC.id === entry.id))) {
        return true;
      }
      return false;
    })
    .map((entry) => {
      const parsedId = parseInt(entry.id, 10);

      return {
        name: entry.name,
        contributor_id: Number.isNaN(parsedId) ? undefined : parsedId,
        dsp_details: [
          {
            dsp: 'APPLE',
            request_new_store_artist_id: entry.appleArtistId === 'new',
            artist_id: entry.appleArtistId === 'new' ? '' : (entry.appleArtistId ?? ''),
          },
          {
            dsp: 'SPOTIFY',
            request_new_store_artist_id: entry.spotifyArtistId === 'new',
            artist_id: entry.spotifyArtistId === 'new' ? '' : (entry.spotifyArtistId ?? ''),
          },
        ],
      };
    });

  const genreIds = pickGenres(isSpokenWord, productData);

  body.genre1_id = genreIds[0];
  body.genre2_id = genreIds[1];
  body.genre3_id = genreIds[2];

  productData.coverImages.forEach((c) => {
    if (c.uploadInfo.status !== 'done' || c.file === undefined) {
      return;
    }

    if (c.type === CoverType.BOOKLET) {
      body.file_imports.push({
        location_uri: c.uploadInfo.objectUri,
        mime_type: c.file.type,
        file_size: c.file.size,
        original_file_name: c.file.name,
        asset_type: c.type,
        asset_title: productData.bookletTitle,
        asset_c_line:
          productData.bookletCYear && productData.bookletCText
            ? `${productData.bookletCYear} ${productData.bookletCText}`
            : productData.bookletCYear || productData.bookletCText,
      });

      return;
    }

    body.file_imports.push({
      location_uri: c.uploadInfo.objectUri,
      mime_type: c.file.type,
      file_size: c.file.size,
      original_file_name: c.file.name,
      asset_type: c.type,
      ai_usage_level: c.type === CoverType.FRONT || c.type === CoverType.BACK ? c.aiUsage : undefined,
    });
  });

  if (isSpokenWord && tracksPreview?.uploadInfo?.status === 'done') {
    body.file_imports.push({
      location_uri: tracksPreview.uploadInfo.objectUri,
      mime_type: tracksPreview.file.type,
      file_size: tracksPreview.file.size,
      original_file_name: tracksPreview.file.name,
      asset_type: 'AUDIO_PREVIEW',
    });
  }

  if (releaseDates.metaReleaseDateToggle) {
    const metaDate = formatDate(releaseDates.metaReleaseDate);
    if (metaDate) {
      body.release_overrides.push({ dsp: 'META', sales_start_date: metaDate });
    }
  }

  if (releaseDates.tikTokReleaseDateToggle) {
    const tiktokDate = formatDate(releaseDates.tikTokReleaseDate);
    if (tiktokDate) {
      body.release_overrides.push({ dsp: 'TIKTOK', sales_start_date: tiktokDate });
    }
  }

  body.release_countries_ids = countrySelection.countries.map((c) => parseInt(c, 10));

  if (!shopSelection.allShops) {
    const retailerList: NonNullable<AlbumRequestDTO['retailers']> = [];
    shopSelection.shopSelection.forEach((s) => {
      if (s.download !== true && s.streaming !== true && s.ringtone !== true && s.special !== true) {
        return;
      }

      const allowed_product_use_types: ('OnDemandStream' | 'PermanentDownload' | 'UseAsRingtone' | string)[] = [];

      if (s.download === true) {
        allowed_product_use_types.push('PermanentDownload');
      }
      if (s.streaming === true) {
        allowed_product_use_types.push('OnDemandStream');
      }
      if (s.ringtone === true) {
        allowed_product_use_types.push('UseAsRingtone');
      }
      if (s.special === true && s.specialType) {
        allowed_product_use_types.push(s.specialType);
      }

      retailerList.push({ retailer_id: String(s.id), allowed_product_use_types });
    });

    body.retailers = retailerList;
    body.auto_deliver_to_new_retailers = shopSelection.sendToNewShops;
  }

  const prereleaseAvailable = releaseDates.preReleaseSongsToggle && tracklisting.length > 1;
  body.tracks = tracklisting.map((t, index) => {
    if (t.existing) {
      return {
        track_id: t.id,
        track_no: index + 1,
        clear_for_download: true,
        clear_for_streaming: true,
        clear_for_ringtone: true,
        clear_for_other_use_types: true,
        sales_start_date_override: formatDate(t.downloadReleaseDate),
        streaming_start_date_override: formatDate(t.streamingReleaseDate),
      };
    }

    const title =
      isSpokenWord && tracksProductTitleAdded
        ? t.title + combineAddableProductTitle(productData.title, productData.titleVersion)
        : t.title;

    const remasteredYear =
      t.metadata.remastered && productType !== 'AUDIOBOOK' ? parseInt(t.metadata.remasteredYear, 10) : NaN;

    const genreIds = pickGenres(isSpokenWord, t.metadata);

    const file_imports =
      t.uploadInfo.status !== 'done' || t.file === undefined
        ? []
        : [
            {
              location_uri: t.uploadInfo.objectUri,
              mime_type: t.file.type,
              file_size: t.file.size,
              original_file_name: t.file.name,
              file_type: 'PRIMARY_TRACK_FILE' as const,
              audio_format: { ...t.formatInfos, codec: 'PCM' },
            },
          ];

    return {
      track_no: index + 1,
      clear_for_download: true,
      clear_for_streaming: true,
      clear_for_ringtone: true,
      clear_for_other_use_types: true,
      sales_start_date_override: prereleaseAvailable ? formatDate(t.downloadReleaseDate) : undefined,
      streaming_start_date_override: prereleaseAvailable ? formatDate(t.streamingReleaseDate) : undefined,
      track: {
        track_type: 'AUDIO',
        isrc: t.isrc,
        script_locale: t.metadata.titleLanguage || undefined,
        audio_locale: t.metadata.lyricsLanguage || undefined,
        title,
        title_version: t.titleVersion,
        price_code: 'Front Plus',
        p_line:
          t.metadata.pYear && t.metadata.pText
            ? `${t.metadata.pYear} ${t.metadata.pText}`
            : t.metadata.pYear || t.metadata.pText,
        publishers: t.metadata.publisher,
        is_remasted_version: productType === 'AUDIOBOOK' ? undefined : t.metadata.remastered,
        remastered_year: Number.isNaN(remasteredYear) ? undefined : remasteredYear,
        is_cover_version: productType === 'AUDIOBOOK' ? undefined : t.metadata.coverVersion,
        is_live_recording: t.metadata.live,
        is_instrumental: productType === 'RADIO_PLAY' ? t.metadata.noSpeaker : t.metadata.instrumental,
        is_a_capella: isSpokenWord ? undefined : t.metadata.acapella,
        is_public_domain: t.metadata.publicDomain,
        lyrics: t.metadata.lyrics,
        content_rating: t.metadata.explicit,
        aiUsage: {
          ai_level_lyrics:
            t.metadata.aiUsageToggle && !t.metadata.instrumental
              ? t.metadata.aiUsageLyrics || undefined
              : KIPolicies.NONE,
          ai_level_audio: t.metadata.aiUsageToggle ? t.metadata.aiUsageAudio || undefined : KIPolicies.NONE,
          ai_level_video: t.metadata.aiUsageToggle && hasVideo ? t.metadata.aiUsageVideo || undefined : KIPolicies.NONE,
          ai_level_composition: t.metadata.aiUsageToggle ? t.metadata.aiUsageComposition || undefined : KIPolicies.NONE,
        },
        preview_snippet_offset: parseInt(t.metadata.snippetStart, 10),
        preview_snippet_duration: parseInt(t.metadata.snippetDuration, 10),
        is_apple_digital_master: overallMetadata.appleDigitalMastered,
        mastering_studio_email: overallMetadata.appleDigitalMastered ? overallMetadata.appleEmail : undefined,
        contributors: mapContributors(t.contributors, contributors),
        genre1_id: genreIds[0],
        genre2_id: genreIds[1],
        genre3_id: genreIds[2],
        file_imports,
      },
    };
  });

  if (neighbouringRightsSelection.evaluate) {
    const tracksNeighbouringRights: NonNullable<AlbumRequestDTO['tracks_neighbouring_rights']> = [];

    for (const c of neighbouringRightsSelection.countries) {
      const assignment = neighbouringRightsSelection.assignments.find((a) => a.id === c);
      // countryShopsValidity blocks delivery without an assignment per country
      if (!assignment) {
        continue;
      }

      const country_id = parseInt(assignment.id, 10);
      const percentage_own = formatFloat(assignment.percentage) || -1;
      const evaluation_start_date = formatDate(assignment.startDate);
      const evaluation_end_date = formatDate(assignment.endDate);

      if (evaluation_start_date === undefined || evaluation_end_date === undefined) {
        const someDate =
          evaluation_start_date ||
          evaluation_end_date ||
          `${new Date().getFullYear()}-${new Date().getMonth() + 1}-${new Date().getDate()}`;

        tracksNeighbouringRights.push({
          country_id,
          percentage_own,
          // Backend is unable to handle missing or invalid dates, so we have to set something or the entry can not be send.
          // Using the same value for both will ensure an error from validation (end has to be after start).
          evaluation_start_date: someDate,
          evaluation_end_date: someDate,
        });
        continue;
      }

      tracksNeighbouringRights.push({ country_id, percentage_own, evaluation_start_date, evaluation_end_date });
    }

    body.tracks_neighbouring_rights = tracksNeighbouringRights;
  }

  return body;
}

export type ValidationResults = {
  valid: boolean;
  errors?: null | { msg: string }[];
  property_validations?: null | Record<
    string,
    {
      valid: boolean;
      errors?: null | { msg: string }[];
      nested_results?: null | ValidationResults[] | ValidationResults;
    }
  >;
};

export function conformsToValidateResultSpec(value: unknown): value is ValidationResults {
  return conformsTo({
    valid: isBoolean,
    errors: isNullable(isArrayAndEveryElement(conformsTo({ msg: isString }))),
    property_validations: isNullable(
      isRecordWith(
        isString,
        conformsTo({
          valid: isBoolean,
          errors: isNullable(isArrayAndEveryElement(conformsTo({ msg: isString }))),
          nested_results: isNullable(
            matchesOneOf(isArrayAndEveryElement(conformsToValidateResultSpec), conformsToValidateResultSpec),
          ),
        }),
      ),
    ),
  })(value);
}

export function anyGivenKeyInvalid(result: ValidationResults, ...keys: string[]): boolean {
  return keys.some((k) => {
    const validation = result.property_validations?.[k];
    if (validation === undefined) {
      return false;
    }

    return !validation.valid;
  });
}
